import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import ActionButton from '@repo/ui/action-button'
import { DeleteIcon, PenIcon, QuestionMark2Icon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'

import { deleteLanguageTestQuestion } from '../../../api/language-test/delete-language-test-question'
import { updateLanguageTestQuestion } from '../../../api/language-test/update-language-test-question'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { LanguageTestQuestion as LanguageTestQuestionType } from '../../../lib/types/models/language-test/language-test-question'
import { queryClient } from '../../../main'
import LanguageTestAnswer from './language-test-answer'
import LanguageTestQuestionCreationForm from './language-test-question-creation-form'

interface LanguageTestQuestionProps {
  question: LanguageTestQuestionType
  index: number
}

const LanguageTestQuestion = ({ question, index }: LanguageTestQuestionProps) => {
  const { modalState, closeModel, toggleModal } =
    useModalManager<'language-test-question-creation'>(['language-test-question-creation'])

  const { mutate: updateQuestion, isPending: isUpdatingQuestion } = useMutation({
    mutationFn: updateLanguageTestQuestion,
    onSuccess: () => {
      closeModel('language-test-question-creation')
      queryClient.invalidateQueries({
        queryKey: ['language-test-section', String(question.languageTestSectionId)]
      })
    }
  })

  const { mutate: deleteQuestion, isPending: isDeletingQuestion } = useMutation({
    mutationFn: deleteLanguageTestQuestion,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['language-test-section', String(question.languageTestSectionId)]
      })
    }
  })

  return (
    <div className="rounded-lg border border-grey-light bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-white-hover border-b border-grey-light">
        <div className="flex items-baseline gap-2">
          <Typography variant="tag" className="whitespace-nowrap">
            Q{index + 1} :{' '}
          </Typography>
          <Typography variant="tag" color={tailwindColors.secondary.DEFAULT}>
            {question.label}
          </Typography>
        </div>

        <div className="flex items-center gap-2">
          <ActionButton
            variant="danger"
            icon={DeleteIcon}
            onClick={() => deleteQuestion(question.id)}
            isLoading={isDeletingQuestion}
          />
          <ActionButton
            variant="primary"
            icon={PenIcon}
            onClick={() => toggleModal('language-test-question-creation')}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 px-3.5 py-3">
        {question.answers.map((answer, index) => (
          <LanguageTestAnswer key={answer.id} answer={answer} index={index} />
        ))}
      </div>

      <Modal
        icon={QuestionMark2Icon}
        isOpen={modalState['language-test-question-creation']}
        onCancel={() => closeModel('language-test-question-creation')}
        title="Modifier une question"
        hideActions
      >
        <LanguageTestQuestionCreationForm
          isEditing
          className={MODAL_BODY_CLASSNAME}
          isLoading={isUpdatingQuestion}
          defaultValues={{
            label: question.label,
            position: question.position,
            answers: question.answers.map((answer) => ({
              id: answer.id,
              label: answer.label,
              isCorrect: answer.isCorrect,
              position: answer.position
            }))
          }}
          onSubmit={(data) => {
            updateQuestion({
              id: question.id,
              sectionId: question.languageTestSectionId,
              ...data
            })
          }}
          onCancel={() => closeModel('language-test-question-creation')}
        />
      </Modal>
    </div>
  )
}

export default LanguageTestQuestion
